<template>
    <v-card flat>
        <ProjectMeetingDetails
            :item="editedItem"
            @save="updateItem"
            v-model="isEditingItem"
        />

        <v-card-text class="py-0">
            <div class="d-flex d-block mx-1 mb-4 pb-2 align-center justify-end">
                <div class="d-flex d-inline">
                    <v-btn 
                        v-if="moduleID"
                        color="info" 
                        class="my-2 ml-3 d-block"
                        title="Create a New Meeting"
                        @click="createItem">
                        New Meeting
                    </v-btn> 
                    <v-btn 
                        v-if="moduleID && projectMeetings"
                        color="info" 
                        class="my-2 ml-3 d-block"
                        title="Save the template"
                        @click="saveTemplate">
                        Save
                    </v-btn>                  
                </div>
            </div>
            <v-data-table
                v-if="projectMeetings"
                :items="projectMeetings"
                :headers="listHeaders"
                :options.sync="options"
                disable-sort
                hide-default-footer
                item-key="name"
            >
                <template v-slot:item="{ item, index }">
                    <tr>
                        <td class="psr_avenir_book">
                            <v-icon
                                class="mr-2"
                                :disabled="index === 0"
                                title="Move up"
                                @click="setItemIndex(item, index-1)"
                            >
                                mdi-arrow-up
                            </v-icon>
                            <v-icon
                                class="mr-2"
                                :disabled="index >= projectMeetings.length - 1"
                                title="Move down"
                                @click="setItemIndex(item, index+1)"
                            >
                                mdi-arrow-down
                            </v-icon>
                        </td>
                        <td class="psr_avenir_book">{{ item.label }}</td>                                                                       
                        <td>
                            <v-icon
                                class="mr-2"
                                title="Edit Meeting"
                                @click="editItem(item)"
                            >
                                mdi-pencil
                            </v-icon>
                            <v-icon
                                class="mr-2"
                                title="Delete Meeting"
                                @click="deleteItem(item)"
                            >
                                mdi-delete
                            </v-icon>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
    import axios from 'axios';
    import Cookie from 'js-cookie';
    import appUrls from '../../helpers/environment';
    import ProjectMeetingDetails from './ProjectMeetingDetails.vue';

    function generateKey() {
        return Math.random().toString(36).substr(2, 9);
    }

    export default {
        name: 'ProjectTemplates',

        props: {
            moduleID: String,
        },

        components: {
            ProjectMeetingDetails
        },

        computed: {
            projectMeetings: {
                get: function () {
                    const meetings = (this.selectedProjectTemplate 
                        && this.selectedProjectTemplate.project_template 
                        && this.selectedProjectTemplate.project_template.meetings) || [];

                    return meetings.map((meeting) => ({
                        ...meeting,
                        key: meeting.key || generateKey()
                    }));
                },
                set: function (value) {
                    this.selectedProjectTemplate.project_template.meetings = [...value];
                }
            }   
        },
        
        data: () => {
            return {
                noProjectTemplate: false,
                editedItem: {},
                isEditingItem: false,
                projectTemplateCache: {},
                selectedProjectTemplate: null,
                listHeaders: [
                    { text: 'Order', width: '100px' },
                    { text: 'Label',  value: 'label',    align: 'start'},
                    { text: 'Actions',     width: '200px', value: 'actions',    align: 'start'}
                ],
                options: {
                    itemsPerPage: -1
                },
            }
        },

        created () {
            this.provisionProjectTemplate();
        },

        methods: {
            updateItem: async function (item) {
                const {isNew} = item;
                const projectMeetingsCopy = [...this.projectMeetings];

                if (isNew) {
                    delete item.isNew;
                    projectMeetingsCopy.push(item);
                } else {
                    const currentIndex = projectMeetingsCopy.findIndex((step) => step.key === item.key);
                    projectMeetingsCopy[currentIndex] = item;
                }

                this.projectMeetings = projectMeetingsCopy;
            },

            provisionProjectTemplate: async function () {
                const module_id = this.moduleID;

                if (!this.projectTemplateCache[module_id]) {
                    // Template is not cached. Fetch it and cache.
                    try {
                        const projectTemplate = await axios.get(`${appUrls.api}/api/project_template/${module_id}`, {
                            headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                        });

                        // console.log(projectTemplate.data)
                        this.projectTemplateCache[module_id] = projectTemplate.data;

                    } catch ( error ) {
                        if (error.response && error.response.status === 404) {
                            this.projectTemplateCache[module_id] = {
                                noProjectTemplate: true
                            };
                        }
                    };
                }

                // Return from cache
                this.selectedProjectTemplate = await this.projectTemplateCache[module_id];
            },

            saveTemplate: async function () {
                try {
                    const projectTemplate = this.selectedProjectTemplate;

                    const saveTemplate = await axios.put(`${appUrls.api}/api/project_template/${this.selectedProjectTemplate.project_template_id}`, projectTemplate, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                    });

                    const status = saveTemplate.status;

                    if (status === 200) {
                        this.$emit('triggerUserFeedback', true, 'Project Meetings Saved');
                    } else {
                        this.$emit('triggerUserFeedback', true, 'There was a problem saving the Project Meetings', 'error');
                    }
                } catch ( error ) {
                    console.log(error);
                };
            },

            createItem() {
                this.editItem({
                    isNew: true,
                    name: '',
                    key: generateKey()
                });
            },

            editItem(item) {
                this.editedItem = {...item};
                this.isEditingItem = true;
            },

            setItemIndex(item, newIndex) {
                const projectMeetingsCopy = [...this.projectMeetings];
                const currentIndex = projectMeetingsCopy.findIndex((meeting) => meeting.key === item.key);
                const newIndexValue = projectMeetingsCopy[newIndex];

                projectMeetingsCopy[newIndex] = item;
                projectMeetingsCopy[currentIndex] = newIndexValue;
                
                this.projectMeetings = projectMeetingsCopy;
            }, 

            deleteItem(item) {
                const projectMeetingsCopy = [...this.projectMeetings];
                const currentIndex = projectMeetingsCopy.findIndex((meeting) => meeting.key === item.key);
                
                projectMeetingsCopy.splice(currentIndex, 1);

                this.projectMeetings = projectMeetingsCopy;
            },
        }
    }
</script>

<style scoped>
    div.listings:nth-of-type(even) {
        /* background-color: #eef7f8; */
        padding: 2px 0;
    }
</style>


// WEBPACK FOOTER //
// src/components/datamanagement/ProjectMeetings.vue